import type { useForgeProviderAccess } from "../../cmmd/forgeProviderAccess";
import { hasPotentialProviderAuthority } from "../../cmmd/forgeProviderAccess";
import type { CloudProjectCatalogRow } from "../../cmmd/cmmdForgeRuntimeListResource";
import type { Project } from "../../types";
import type { DesktopRuntimeInventoryProjectSelection } from "./NewThreadLanding.submission";
import type { SubmitNewThreadInput } from "./NewThreadLanding.submission";
import {
  canHostScratchWorkspace,
  resolveScratchTargetEnvironmentId,
  type WorktreeMode,
} from "./NewThreadLanding.logic";

export type DraftSubmissionTarget =
  | { readonly kind: "scratch"; readonly environmentId: string }
  | { readonly kind: "cloud"; readonly row: CloudProjectCatalogRow }
  | { readonly kind: "project"; readonly project: Project }
  | { readonly kind: "desktop"; readonly project: DesktopRuntimeInventoryProjectSelection }
  | { readonly kind: "none" };

export interface DraftSubmissionDecision {
  /** The concrete target the submit path will use, or why no target exists. */
  readonly target: DraftSubmissionTarget;
  readonly scratchWorkspaceSupported: boolean;
  readonly canResolveModelOnSubmit: boolean;
  readonly hasSendableContent: boolean;
  /** The normalized write input. The hook only adds progress reporting. */
  readonly submission: Omit<SubmitNewThreadInput, "onProgress">;
}

/** The hook's input, including the extra facts only the decision needs. */
export interface DraftSubmitInput extends Omit<
  SubmitNewThreadInput,
  "envMode" | "includeBranch" | "onProgress"
> {
  /** Captures source content after validation and before thread creation begins. */
  readonly onSubmissionStarted?: (() => void) | undefined;
  readonly worktreeMode: WorktreeMode;
  readonly branchUserSelected: boolean;
  readonly projectDefaultBranch: string | null;
  readonly providerAccess: ReturnType<typeof useForgeProviderAccess>;
  readonly inventoryKnown: boolean;
}

/**
 * The draft's complete send decision.
 *
 * Rendering and submission need the same answer about a target, model, and
 * message. Keeping that answer in one value means a send cannot follow a
 * different branch from the button that enabled it.
 */
export function resolveDraftSubmissionDecision(input: DraftSubmitInput): DraftSubmissionDecision {
  const {
    worktreeMode,
    branchUserSelected,
    projectDefaultBranch,
    onSubmissionStarted: _onSubmissionStarted,
    providerAccess: _providerAccess,
    inventoryKnown: _inventoryKnown,
    ...submission
  } = input;
  const scratchWorkspaceSupported = canHostScratchWorkspace({
    primaryEnvironmentId: input.primaryEnvironmentId,
    primaryIsWorkspaceProxy: input.primaryIsWorkspaceProxy,
    selectedExecutionId: input.selectedExecutionId,
  });
  const target = resolveDraftSubmissionTarget(input);
  const canResolveModelOnSubmit = canResolveDraftModelOnSubmit(input);
  const plan = {
    envMode: worktreeMode === "local" ? "local" : "worktree",
    includeBranch:
      worktreeMode !== "local" && (branchUserSelected || projectDefaultBranch !== null),
    prompt: input.prompt.trim(),
  } as const;

  return {
    target,
    scratchWorkspaceSupported,
    canResolveModelOnSubmit,
    hasSendableContent:
      target.kind !== "none" &&
      canResolveModelOnSubmit &&
      (plan.prompt.length > 0 || (input.images?.length ?? 0) > 0 || (input.files?.length ?? 0) > 0),
    submission: {
      ...submission,
      noWorkspaceSelected: target.kind === "scratch",
      activeProject:
        target.kind === "cloud" || target.kind === "project" ? input.activeProject : null,
      activeCloudCatalogRow: target.kind === "cloud" ? target.row : null,
      activeDesktopInventoryProject: target.kind === "desktop" ? target.project : null,
      envMode: plan.envMode,
      includeBranch: plan.includeBranch,
      prompt: plan.prompt,
    },
  };
}

function canResolveDraftModelOnSubmit(input: DraftSubmitInput): boolean {
  if (input.providerAccess.status !== "ready") return false;
  if (input.selectedModelSelection !== null) return true;
  if (input.inventoryKnown || !hasPotentialProviderAuthority(input.providerAccess)) return false;
  // Keep the established optimistic path for a selected Cloud execution. A
  // connected Checkout can lack a catalog row while its runtime warms.
  return input.selectedExecutionId === "cloud" || input.activeDesktopInventoryProject !== null;
}

function resolveDraftSubmissionTarget(input: DraftSubmitInput): DraftSubmissionTarget {
  if (input.noWorkspaceSelected) {
    const environmentId = resolveScratchTargetEnvironmentId({
      primaryEnvironmentId: input.primaryEnvironmentId,
      primaryIsWorkspaceProxy: input.primaryIsWorkspaceProxy,
      selectedExecutionId: input.selectedExecutionId,
    });
    return environmentId === null ? { kind: "none" } : { kind: "scratch", environmentId };
  }
  if (input.activeCloudCatalogRow !== null)
    return { kind: "cloud", row: input.activeCloudCatalogRow };
  if (input.activeProject !== null) return { kind: "project", project: input.activeProject };
  if (input.activeDesktopInventoryProject !== null) {
    return { kind: "desktop", project: input.activeDesktopInventoryProject };
  }
  return { kind: "none" };
}
